import { radius } from '@alison/ui';
import { useTenant } from '@alison/whitelabel/react';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PeopleIcon from '@mui/icons-material/People';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { CadencePickerInline } from './CadencePickerInline.js';
import { eventGroupsFor, ROLES } from './notifications.data.js';
import type { CadenceId, ChannelId, EventPrefs, OrgPrefs, RoleId } from './notifications.types.js';
import { SlackIcon } from './SlackIcon.js';

interface PrefsGridProps {
  prefs: OrgPrefs;
  role: RoleId;
  onRoleChange: (role: RoleId) => void;
  onToggle: (role: RoleId, event: string, channel: ChannelId) => void;
  onCadenceChange: (role: RoleId, event: string, cadence: CadenceId) => void;
  slackConnected: boolean;
  emailConnected: boolean;
  saving: boolean;
}

const CHANNEL_COLS: { key: ChannelId; label: string; icon: ReactNode }[] = [
  { key: 'slack', label: 'Slack', icon: <SlackIcon sx={{ fontSize: 14 }} /> },
  { key: 'email', label: 'Email', icon: <EmailIcon sx={{ fontSize: 14 }} /> },
  { key: 'inapp', label: 'In-app', icon: <NotificationsIcon sx={{ fontSize: 14 }} /> },
];

const GRID_COLS = 'minmax(220px, 1fr) 150px 92px 92px 92px';

const headLabelSx = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
};

export function PrefsGrid({
  prefs,
  role,
  onRoleChange,
  onToggle,
  onCadenceChange,
  slackConnected,
  emailConnected,
  saving,
}: PrefsGridProps): ReactNode {
  const { providerName } = useTenant();
  const currentPrefs: EventPrefs = prefs[role];

  const channelDisabled = (key: ChannelId): boolean => {
    if (key === 'slack') return !slackConnected;
    if (key === 'email') return !emailConnected;
    return false;
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Notification preferences
      </Typography>

      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: `${radius.xxl}px`,
          bgcolor: 'primary.light',
          flexWrap: 'wrap',
        }}
      >
        <PeopleIcon sx={{ fontSize: 14, flexShrink: 0, color: 'primary.main' }} />
        <Typography sx={{ fontSize: 12, color: 'primary.dark', flex: 1 }}>
          Your defaults for each viewing role. Changes apply to your account.
        </Typography>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            color: 'text.secondary',
            ml: 'auto',
          }}
        >
          Applies to
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={role}
          onChange={(_, nextRole: RoleId) => nextRole && onRoleChange(nextRole)}
        >
          {ROLES.map((roleOption) => (
            <ToggleButton key={roleOption.id} value={roleOption.id}>
              {roleOption.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: `${radius.xxl}px`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: GRID_COLS,
            alignItems: 'center',
            bgcolor: 'background.platform',
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 2,
            minHeight: 40,
          }}
        >
          <Typography sx={headLabelSx}>Event</Typography>
          <Typography sx={{ ...headLabelSx, pl: 1 }}>Cadence</Typography>
          {CHANNEL_COLS.map((col) => (
            <Stack
              key={col.key}
              direction="row"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                opacity: channelDisabled(col.key) ? 0.4 : 1,
              }}
            >
              {col.icon}
              <Typography sx={headLabelSx}>{col.label}</Typography>
              {channelDisabled(col.key) && (
                <Box
                  component="span"
                  sx={{
                    fontSize: 9,
                    bgcolor: 'background.muted',
                    color: 'text.disabled',
                    px: 1,
                    py: 1,
                    borderRadius: `${radius.lg}px`,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}
                >
                  off
                </Box>
              )}
            </Stack>
          ))}
        </Box>

        {eventGroupsFor(providerName).map((group) => (
          <Box key={group.id}>
            <Box
              sx={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'text.secondary',
                bgcolor: 'background.page',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              {group.label}
            </Box>
            {group.events.map((event) => {
              const pref = currentPrefs[event.id as keyof EventPrefs];
              return (
                <Box
                  key={event.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: GRID_COLS,
                    alignItems: 'center',
                    px: 2,
                    minHeight: 60,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                    '&:hover': { bgcolor: 'background.platform' },
                  }}
                >
                  <Stack sx={{ py: 2, pr: 3, gap: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                      {event.name}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {event.description}
                    </Typography>
                  </Stack>

                  <Box>
                    <CadencePickerInline
                      value={pref.cadence}
                      disabled={saving}
                      onChange={(cadence) => onCadenceChange(role, event.id, cadence)}
                    />
                  </Box>

                  {CHANNEL_COLS.map((col) => (
                    <Box
                      key={col.key}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Switch
                        checked={pref[col.key]}
                        disabled={saving || channelDisabled(col.key)}
                        onChange={() => onToggle(role, event.id, col.key)}
                        slotProps={{ input: { 'aria-label': `${event.name} ${col.label}` } }}
                      />
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
